<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { tokenManager } from '@/services/tokenManager'
import { sessionManager } from '@/services/sessionManager'
import AppLayout from './components/AppLayout.vue'
import ErrorBoundary from './components/common/ErrorBoundary.vue'
import Toast from 'primevue/toast'
import ProgressBar from 'primevue/progressbar'

const router = useRouter()
const authStore = useAuthStore()
const isLoading = ref(false)

// Initialize authentication state from stored tokens
onMounted(async () => {
  console.log('[App] Initializing authentication state...')
  
  // Try to restore auth state from stored tokens
  const accessToken = tokenManager.getAccessToken()
  const refreshToken = tokenManager.getRefreshToken()
  const idToken = tokenManager.getIdToken()
  
  if (accessToken && refreshToken && idToken) {
    // Check if access token is expired
    if (!tokenManager.isTokenExpired(accessToken)) {
      console.log('[App] Found valid tokens, restoring auth state')
      
      try {
        // Restore auth store from tokens
        authStore.setTokens({
          accessToken,
          refreshToken,
          idToken,
        })
        
        // Start session monitoring for automatic token refresh
        sessionManager.startMonitoring()
        
        console.log('[App] Auth state restored successfully')
      } catch (error) {
        console.error('[App] Error restoring auth state:', error)
        // Clear invalid tokens
        tokenManager.clearTokens()
        authStore.clearAuth()
      }
    } else {
      console.log('[App] Access token expired, will attempt refresh on next navigation')
      // Don't clear tokens yet - the auth guard will attempt to refresh
    }
  } else {
    console.log('[App] No stored tokens found')
  }
})

// Show loading indicator during route transitions
router.beforeEach(() => {
  isLoading.value = true
})

router.afterEach(() => {
  isLoading.value = false
})

// Transition hooks for smooth animations
const onBeforeEnter = (el: Element) => {
  const element = el as HTMLElement
  element.style.opacity = '0'
  element.style.transform = 'translateY(10px)'
}

const onEnter = (el: Element, done: () => void) => {
  const element = el as HTMLElement
  element.offsetHeight // Trigger reflow
  element.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
  element.style.opacity = '1'
  element.style.transform = 'translateY(0)'
  
  setTimeout(done, 300)
}

const onLeave = (el: Element, done: () => void) => {
  const element = el as HTMLElement
  element.style.transition = 'opacity 0.2s ease, transform 0.2s ease'
  element.style.opacity = '0'
  element.style.transform = 'translateY(-10px)'
  
  setTimeout(done, 200)
}
</script>

<template>
  <!-- Skip to main content link for keyboard navigation -->
  <a href="#main-content" class="skip-link">Skip to main content</a>
  
  <!-- Route loading indicator -->
  <Transition name="fade">
    <div v-if="isLoading" class="route-loading-indicator">
      <ProgressBar mode="indeterminate" style="height: 3px" />
    </div>
  </Transition>
  
  <AppLayout>
    <Toast position="top-right" />
    <ErrorBoundary>
      <RouterView v-slot="{ Component, route }">
        <Transition 
          :name="(route.meta.transition as string) || 'fade'" 
          mode="out-in"
          @before-enter="onBeforeEnter"
          @enter="onEnter"
          @leave="onLeave"
        >
          <component :is="Component" :key="route.path" />
        </Transition>
      </RouterView>
    </ErrorBoundary>
  </AppLayout>
</template>

<style>
/* Global styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Nunito', Arial, sans-serif;
  color: #2f405b;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

h1, h2, h3, h4, h5, h6 {
  font-family: 'Inter', Arial, sans-serif;
  font-weight: 600;
}

code, pre {
  font-family: 'Consolas', 'Courier New', monospace;
}

/* Route loading indicator */
.route-loading-indicator {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
}

/* Route transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* Scale transition for modals/dialogs */
.scale-enter-active,
.scale-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
}
</style>
